import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';

import { type CanaryRecommendation, generateAEOSolution } from '@/api/aiVisibility';
import StarsIcon from '@/assets/svg/ai-stars.svg?react';
import { Button } from '@/components/buttons';
import { LoadingText } from '@/components/loaders';
import { Markdown } from '@/components/page/ai';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/Sheet';
import { Link, Text, Title } from '@/components/typography';
import { ROUTES } from '@/router';

import RecommendationTags from './RecommendationTags';

type Props = {
  recommendation: CanaryRecommendation | null;
  onClose: () => void;
  onSolutionGenerated: (recommendationId: string, solutionMarkdown: string) => Promise<void>;
};

const AIVisibilityRecommendationSheet = ({
  recommendation,
  onClose,
  onSolutionGenerated,
}: Props) => {
  const suggestSolutionRef = useRef<HTMLButtonElement>(null);
  const solutionHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusSolutionId = useRef<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [failedRecommendationId, setFailedRecommendationId] = useState<string | null>(null);
  const solutionMarkdown = recommendation?.solutionMarkdown;
  useEffect(() => {
    if (
      generatingId === recommendation?.id ||
      (solutionMarkdown != null && recommendation?.id === focusSolutionId.current)
    ) {
      solutionHeadingRef.current?.focus();
      if (solutionMarkdown != null) focusSolutionId.current = null;
    } else if (failedRecommendationId === recommendation?.id) {
      suggestSolutionRef.current?.focus();
    }
  }, [failedRecommendationId, generatingId, recommendation?.id, solutionMarkdown]);
  const handleSuggestSolution = async () => {
    if (!recommendation) return;

    const recommendationId = recommendation.id;
    setGeneratingId(recommendationId);
    setFailedRecommendationId(null);

    try {
      const generatedSolution = await generateAEOSolution(recommendationId);
      focusSolutionId.current = recommendationId;
      await onSolutionGenerated(recommendationId, generatedSolution);
    } catch {
      setFailedRecommendationId(recommendationId);
    } finally {
      setGeneratingId(null);
    }
  };
  const renderField = (value: string | null) =>
    value ? (
      <Markdown variant="label" externalLinks>
        {value}
      </Markdown>
    ) : (
      <p>
        <Text variant="label" weight="regular">
          <Trans>Not available</Trans>
        </Text>
      </p>
    );
  const relatedPrompts = [
    ...new Map(
      recommendation?.promptResults?.map((result) => [result.promptId, result.promptText]) ?? [],
    ).entries(),
  ];

  return (
    <Sheet
      open={recommendation != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        initialFocus={solutionMarkdown != null ? undefined : suggestSolutionRef}
        className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-225"
      >
        {recommendation && (
          <>
            <SheetHeader className="gap-3 pt-14 pb-6">
              <SheetTitle
                render={(props) => (
                  <Title {...props} level={2} variant="label" color="black" className="pr-8" />
                )}
              >
                {recommendation.title}
              </SheetTitle>
              <RecommendationTags recommendation={recommendation} />
            </SheetHeader>
            <div className="flex flex-col gap-6 overflow-y-auto p-6">
              <section>
                <Title level={3} variant="label" className="mb-2">
                  <Trans>Diagnosis</Trans>
                </Title>
                {renderField(recommendation.diagnosis)}
              </section>
              <section>
                <Title level={3} variant="label" className="mb-2">
                  <Trans>Action</Trans>
                </Title>
                {renderField(recommendation.action)}
              </section>
              <section>
                <Title level={3} variant="label" className="mb-2">
                  <Trans>Why this matters</Trans>
                </Title>
                {renderField(recommendation.purpose)}
              </section>
              {relatedPrompts.length > 0 && (
                <section>
                  <Title level={3} variant="label" className="mb-2">
                    <Plural
                      value={relatedPrompts.length}
                      one="Related Source"
                      other="Related Sources"
                    />
                  </Title>
                  <ul className="list-disc space-y-1 pl-5">
                    {relatedPrompts.map(([promptId, promptText]) => (
                      <li key={promptId}>
                        <Link
                          variant="label"
                          className="cursor-pointer"
                          to={{
                            pathname: ROUTES.aiVisibilityPrompts.path,
                            search: `?${new URLSearchParams({ promptId })}`,
                          }}
                        >
                          {promptText}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {solutionMarkdown != null || generatingId === recommendation.id ? (
                <section>
                  <Title
                    ref={solutionHeadingRef}
                    level={3}
                    variant="label"
                    tabIndex={-1}
                    className="mb-2"
                  >
                    <Trans>Recommendation to Increase your AI Visibility</Trans>
                  </Title>
                  <div className="rounded-[20px] border-3 border-dashed border-green-400/50 bg-green-100/50 p-4 text-gray-900">
                    {solutionMarkdown != null ? (
                      <Markdown variant="label" externalLinks>
                        {solutionMarkdown}
                      </Markdown>
                    ) : (
                      <p role="status">
                        <LoadingText label={t`Generating solution`} />
                      </p>
                    )}
                  </div>
                </section>
              ) : (
                <div className="flex flex-col items-start gap-2">
                  <Button
                    ref={suggestSolutionRef}
                    variant="primary"
                    className="self-start"
                    disabled={generatingId != null}
                    onClick={handleSuggestSolution}
                  >
                    <StarsIcon />
                    <Trans>Suggest Solution</Trans>
                  </Button>
                  {failedRecommendationId === recommendation.id && (
                    <p role="alert" className="text-sm text-red-600">
                      <Trans>Unable to suggest a solution. Please try again.</Trans>
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AIVisibilityRecommendationSheet;
